/**
 * StrategyGroundingPanel
 *
 * Read-only "what the AI will actually see" panel for block screen generation.
 * Replaces the dead data pickers in BlockScreenGenerationDialog: the server
 * resolves ALL generation context from the strategy snapshot
 * (contextOwnedByServer) and ignores request-side picker selections, so the
 * honest UI shows the server-resolved grounding instead of editable inputs
 * the backend discards.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Chip, Divider, Skeleton, Stack, Typography } from '@mui/material';
import { SAccordion } from '@modules/ui-components';
import { STRATEGY_CANVAS } from '@test-kit/selectors/strategy-canvas';
import { InsightsFirestoreService } from '@modules/insights-analyzer/services/InsightsFirestoreService';

export interface StrategyGroundingVariable {
  id: string;
  router?: boolean;
}

export interface StrategyGroundingData {
  /** strategy.inputManifest.insightsAnalysisId — absent = legacy fallback. */
  analysisId?: string;
  /** Router variable id (e.g. selectedTaskId) when the block branches on it. */
  routerVariableId?: string;
  /** Task-route labels: all router options for a router block, or the block's own route. */
  taskRouteLabels: string[];
  /** True when this block collects the router variable / branches per route. */
  isRouterBlock: boolean;
  /** Compact social-proof lines from the strategy source data. */
  socialProof: string[];
  variablesBefore: string[];
  variablesCollected: StrategyGroundingVariable[];
}

/** HB-5: live counts of ONE overridable section — the panel's per-section line. */
export interface StrategyGroundingCounts {
  selected: number;
  total: number;
}

export interface StrategyGroundingPanelProps extends StrategyGroundingData {
  projectId: string;
  /** C7-3 (AC-C10): true when the operator changed the override selection —
   * the panel must label the run honestly instead of claiming server truth. */
  overrideActive?: boolean;
  /**
   * HB-5: the two sections an override can replace. The panel cannot claim to
   * show «what the AI will actually see» while the thesis narrowing is
   * invisible, and only these two ever take the «overridden for this run» tag —
   * task routes and variables are STRUCTURAL (designer invariant 2).
   */
  thesisCounts?: StrategyGroundingCounts;
  socialProofCounts?: StrategyGroundingCounts;
  /**
   * F-LIVE2-45: which plane the operator ACTUALLY changed. Tagging both because
   * an override is active told the operator they had replaced a plane they never
   * touched — accurate to the old wire, misleading about their own decision.
   * Ruling FW9-R2: the panel never claims a change the operator did not make.
   * Both default to `overrideActive`, so a caller that knows nothing per plane
   * keeps the previous behaviour.
   */
  thesesOverridden?: boolean;
  socialProofOverridden?: boolean;
}

interface AnalysisSummary {
  name?: string;
  status?: string;
}

const SECTION_LABEL_SX = { letterSpacing: '0.08em', fontWeight: 600 } as const;

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="overline" color="text.secondary" sx={SECTION_LABEL_SX}>
    {children}
  </Typography>
);

/**
 * HB-5 / F-LIVE2-36: the warning tone is the OUTLINED chip. The filled warning
 * chip this dialog shipped puts white on `#ed6c02` at 3.2:1 and fails AA.
 */
const OverriddenTag: React.FC<{ 'data-testid': string }> = ({ 'data-testid': testId }) => (
  <Chip
    size="small"
    variant="outlined"
    color="warning"
    label="overridden for this run"
    data-testid={testId}
  />
);

/** One section label, with the tag only the overridable sections ever carry. */
const SectionHeader: React.FC<{
  label: string;
  tagged?: boolean;
  tagTestId?: string;
}> = ({ label, tagged = false, tagTestId }) => (
  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
    <SectionLabel>{label}</SectionLabel>
    {tagged && tagTestId && <OverriddenTag data-testid={tagTestId} />}
  </Stack>
);

export const StrategyGroundingPanel: React.FC<StrategyGroundingPanelProps> = ({
  projectId,
  analysisId,
  routerVariableId,
  taskRouteLabels,
  isRouterBlock,
  socialProof,
  variablesBefore,
  variablesCollected,
  overrideActive = false,
  thesisCounts,
  socialProofCounts,
  thesesOverridden,
  socialProofOverridden,
}) => {
  // F-LIVE2-45: per-plane truth, with the pair-wide flag as the fallback.
  const thesesTagged = thesesOverridden ?? overrideActive;
  const socialProofTagged = socialProofOverridden ?? overrideActive;
  const [analysis, setAnalysis] = useState<AnalysisSummary | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  useEffect(() => {
    if (!analysisId) {
      setAnalysis(null);
      return;
    }
    let cancelled = false;
    setAnalysisLoading(true);
    const service = new InsightsFirestoreService(projectId);
    service.get(analysisId)
      .then((doc) => {
        if (!cancelled) setAnalysis(doc ? { name: doc.name, status: doc.status } : null);
      })
      .catch(() => {
        if (!cancelled) setAnalysis(null);
      })
      .finally(() => {
        if (!cancelled) setAnalysisLoading(false);
      });
    return () => { cancelled = true; };
  }, [analysisId, projectId]);

  const statusChipColor = useMemo(() => {
    if (analysis?.status === 'completed') return 'success' as const;
    if (analysis?.status === 'failed') return 'error' as const;
    return 'default' as const;
  }, [analysis?.status]);

  return (
    <SAccordion title="Strategy grounding" collapsible defaultExpanded>
      <Stack spacing={2} data-testid="strategy-grounding-panel">
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {overrideActive
              ? 'Data override active — the AI will see your picked data instead of the server-resolved strategy grounding below.'
              : 'What the AI will actually see — resolved by the server for this block.'}
          </Typography>
          <Chip
            size="small"
            color={overrideActive ? 'warning' : 'default'}
            variant="outlined"
            label={overrideActive ? 'override' : 'server-resolved'}
            data-testid="strategy-grounding-origin-label"
          />
        </Stack>

        <Stack spacing={1}>
          <SectionLabel>Pinned insights analysis</SectionLabel>
          {analysisId ? (
            analysisLoading ? (
              <Skeleton variant="text" width={280} data-testid="strategy-grounding-analysis-loading" />
            ) : (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="body2" data-testid="strategy-grounding-analysis-name">
                  {analysis?.name || analysisId}
                </Typography>
                {analysis?.status && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color={statusChipColor}
                    label={analysis.status}
                  />
                )}
              </Stack>
            )
          ) : (
            <Alert severity="warning" data-testid="strategy-grounding-no-analysis">
              This strategy has no pinned insights analysis — generation will use legacy
              audience/task summaries.
            </Alert>
          )}
          {analysisId && (
            <Typography variant="caption" color="text.secondary">
              Pinned via the strategy input manifest
            </Typography>
          )}
        </Stack>

        <Divider />

        {/* HB-5: the narrowing the run is grounded in — the panel's first
            overridable section. Open question 4, answered «ship it with HB-5». */}
        <Stack spacing={1}>
          <SectionHeader
            label="Insight theses"
            tagged={thesesTagged}
            tagTestId={STRATEGY_CANVAS.strategyGroundingThesesTag}
          />
          {/* Fix round 1 (I1): absent counts mean the theses of a PINNED
              analysis are still being read — claiming «No theses» there would
              be a statement about a list nobody has seen (FP-7). */}
          {!thesisCounts && analysisId ? (
            <Skeleton variant="text" width={220} data-testid={STRATEGY_CANVAS.strategyGroundingTheses} />
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid={STRATEGY_CANVAS.strategyGroundingTheses}
            >
              {!thesisCounts || thesisCounts.total === 0
                ? 'No theses — the snapshot\'s summaries ground this run'
                : `${thesisCounts.selected} of ${thesisCounts.total} theses ${thesesTagged
                  ? '— operator selection for this run'
                  : 'of the pinned analysis'}`}
            </Typography>
          )}
        </Stack>

        <Divider />

        <Stack spacing={1}>
          <SectionLabel>Task route context</SectionLabel>
          <Typography variant="body2" color="text.secondary">
            {isRouterBlock && routerVariableId
              ? `Router block — branches on ${routerVariableId}`
              : taskRouteLabels.length === 1
                ? 'This block belongs to one task route'
                : 'Common path — shown to every route'}
          </Typography>
          {taskRouteLabels.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {taskRouteLabels.map((label) => (
                <Chip key={label} size="small" label={label} />
              ))}
            </Stack>
          )}
        </Stack>

        <Divider />

        <Stack spacing={1}>
          <SectionHeader
            label="Social proof"
            tagged={socialProofTagged}
            tagTestId={STRATEGY_CANVAS.strategyGroundingSocialProofTag}
          />
          {socialProofCounts && socialProofCounts.total > 0 && (
            <Typography variant="body2" color="text.secondary">
              {`${socialProofCounts.selected} of ${socialProofCounts.total} items ${socialProofTagged
                ? '— operator selection for this run'
                : 'of the strategy\'s social proof'}`}
            </Typography>
          )}
          {socialProof.length > 0 ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {socialProof.map((line) => (
                <Chip key={line} size="small" variant="outlined" label={line} />
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              None in the strategy source data — the AI must not invent any.
            </Typography>
          )}
        </Stack>

        <Divider />

        <Stack spacing={1}>
          <SectionLabel>Variables</SectionLabel>
          <Typography variant="body2" color="text.secondary">
            {`Collected before this block: ${variablesBefore.length > 0 ? variablesBefore.join(', ') : 'none'}`}
          </Typography>
          {variablesCollected.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {variablesCollected.map((variable) => (
                <Chip
                  key={variable.id}
                  size="small"
                  label={variable.router ? `${variable.id} (router)` : variable.id}
                  sx={{ fontFamily: 'monospace' }}
                />
              ))}
            </Stack>
          )}
        </Stack>

        {/* HB-5: task routes and variables are STRUCTURAL — they are never
            tagged, because no override can replace them (invariant 2). */}
        <Typography variant="caption" color="text.secondary">
          Read-only — resolved by the server at generation time. Task routes and variables are
          structural: an override replaces source data only.
        </Typography>
      </Stack>
    </SAccordion>
  );
};

StrategyGroundingPanel.displayName = 'StrategyGroundingPanel';
